import {
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { payRateModStyles } from './PayRateMod.styles';
import { FormInputCheckbox } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PersonalInfoTab/Form/Controls/FormInputCheckbox';
import { payrollType, RegularRate, ContractChargeAmount, ContractRegularRate, chargePremium } from 'app/models/Placement/PlacementMods';
import { validDecimal } from 'app/helpers/numberHelper';
import { add, subtract } from 'lodash';

export const PayRateType = ({ isExpanded, isModEdit }: { isExpanded: boolean; isModEdit: boolean }) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext();
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const handleRadioChange = e => {
    setValue(payrollType, e.target.value);
  };
  const { classes } = payRateModStyles({ isExpanded });
  const isChecked = useWatch<boolean>({ name: 'tempRate' });
  const payRoll: any = useWatch({ name: payrollType });
  const regularRateValue = useWatch<number>({ name: RegularRate });
  const contractChargeAmount = useWatch<number>({ name: ContractChargeAmount });
  const contractRegularRate = useWatch<number>({ name: ContractRegularRate });

  const handleRegularRateUpdate = (e) => {
    if (validDecimal(e.target.value, { numberLength: 6, decimalLength: 2 })) {
        setValue('regularRate', e.target.value,{ shouldValidate: true, shouldDirty: true });
        // Calculate and update chargePremium
        if ((contractChargeAmount ?? 0) > 0) {
            const value = e.target.value === null || e.target.value === undefined || e.target.value === '' ? '0' : e.target.value;
            const calculation = add(
              subtract(contractChargeAmount ?? 0, contractRegularRate ?? 0),
              parseFloat(value ?? 0)
            );
            const chargeCalculation = parseFloat(calculation?.toFixed(2));
            setValue(chargePremium, chargeCalculation.toFixed(2), { shouldDirty: true, shouldTouch: true, shouldValidate: true });
          } else {
            setValue(chargePremium, '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
          }
        }
  };

  useEffect(() => {
    if (!isInitialLoad && regularRateValue) {
      handleRegularRateUpdate({ target: { value: regularRateValue } });
    }
    setIsInitialLoad(false);
  }, [regularRateValue]);

  return (
    <Grid className={classes.payRateTypeFlex}>
      <Grid alignSelf="end">
        <Controller
          control={control}
          name={payrollType}
          rules={{ required: true }}
          render={({ field: { onChange, value, ...rest } }) => {
            return (
              <FormControl disabled={!isModEdit}>
                <div className={classes.headerLeft}>
                  <div>
                    <Typography component="span" style={{ color: 'theme.palette.system.darkGray' }}>{`${t(
                      'placement.placementMods.payrollType',
                    )}`}</Typography>
                  </div>
                  <div style={{ marginTop: '-7px' }}>
                    <RadioGroup
                      row
                      value={getValues(payrollType)}
                      name={payrollType}
                      onChange={e => {
                        handleRadioChange(e);
                        onChange(e);
                      }}
                    >
                      <FormControlLabel
                        value="A"
                        checked={payRoll === 'A' ? true : false}
                        control={<Radio disableFocusRipple disableRipple disableTouchRipple />}
                        label={'Company'}
                        className={classes.radioLabel}
                      />
                      <FormControlLabel
                        value="C"
                        checked={payRoll === 'C' ? true : false}
                        control={<Radio disableFocusRipple disableRipple disableTouchRipple />}
                        label={'Client'}
                        className={classes.radioLabel}
                      />
                    </RadioGroup>
                  </div>
                </div>
              </FormControl>
            );
          }}
        />
      </Grid>
      <Grid>
        <Controller
          name={'regularRate'}
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value, ...rest } }) => (
            <TextField
              variant="filled"
              className={classes.rateField}
              label={`${t(`placement.placementMods.${RegularRate}`)}`}
              InputLabelProps={{
                shrink: getValues('regularRate') ? true : false,
              }}
              value={regularRateValue}
              onChange={handleRegularRateUpdate}
              InputProps={{
                readOnly: !isModEdit,
                style: {
                  pointerEvents: isModEdit ? 'auto' : 'none',
                },
                startAdornment: (
                  <>
                    {getValues('regularRate') && (
                      <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                    )}
                  </>
                ),
                sx: { fontSize: '14px' },
              }}
              {...rest}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} style={{ marginTop: '12px' }}>
        <Controller
          name="tempRate"
          control={control}
          render={({ field: { onChange, value, ...rest } }) => {
            return (
              <FormInputCheckbox
                name={`tempRate`}
                isChecked={isChecked}
                handleChecked={value => {
                  onChange(value);
                  setValue('tempRate', value);
                }}
                checkboxLabel={`${t('placement.placementMods.temporaryRate')}`}
                labelClass={classes.checkBoxLabel}
                isDisabled={!isModEdit}
              ></FormInputCheckbox>
            );
          }}
        />
      </Grid>
    </Grid>
  );
};
